import {Reducer} from 'redux';
import {persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {FETCH_DATA_FAILURE, FETCH_DATA_REQUEST, FETCH_DATA_SUCCESS} from './actions';

export interface StatisticsState {
    loading: boolean; // Indicates whether a data fetch operation is in progress
    data: any | null; // Holds the fetched data or null if no data is available
    error?: string; // Stores the error message if a fetch operation fails
}

const initialState: StatisticsState = {
    loading: false,
    data: null,
    error: ''
};
/**
 * plotsReducer
 *
 * A Redux reducer to manage the state of statistics or plots data.
 *
 * State Structure:
 * - `loading`: A boolean flag indicating whether data is currently being fetched.
 * - `data`: Holds the data fetched successfully or `null` if no data is available.
 * - `error`: Contains an error message string if a fetch operation fails, or an empty string otherwise.
 *
 * Actions Handled:
 * - `FETCH_DATA_REQUEST`: Sets `loading` to `true` and keeps existing state for `data` and `error`.
 * - `FETCH_DATA_SUCCESS`: Updates `data` with the fetched results, resets `error`, and sets `loading` to `false`.
 * - `FETCH_DATA_FAILURE`: Clears `data`, updates `error` with the error message, and sets `loading` to `false`.
 *
 * @param {StatisticsState} state - The current state of the statistics module.
 * @param {object} action - The dispatched action containing a `type` and optional `payload`.
 *
 * @returns {StatisticsState} The updated state based on the action type.
 */
const plotsReducer: Reducer = (state = initialState, action) => {
    switch (action.type) {
        case FETCH_DATA_REQUEST:
            return {
                ...state,
                loading: true
            }
        case FETCH_DATA_SUCCESS:
            return {
                loading: false,
                data: action.payload,
                error: ''
            }
        case FETCH_DATA_FAILURE:
            return {
                loading: false,
                data: null,
                error: action.payload
            }
        default:
            return state
    }
};

const persistConfig = {
    key: 'plots',
    storage,
};

// Apply the persist configuration to the authReducer
const persistedPlotsReducer = persistReducer(persistConfig, plotsReducer);

export default persistedPlotsReducer;